/**
 * Utility functions for handling API errors and converting them to user-friendly messages
 */

interface ApiErrorResponse {
  detail?: string;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
  [key: string]: unknown;
}

/**
 * Extracts a user-friendly error message from an API error response
 * @param error - The error object from a failed API call
 * @returns A user-friendly error message string
 */
export function getErrorMessage(error: unknown): string {
  // Handle non-Error objects
  if (!error) {
    return 'An unexpected error occurred';
  }

  // Handle Error instances
  if (error instanceof Error) {
    // Try to parse JSON from the error message
    try {
      const parsed = JSON.parse(error.message) as ApiErrorResponse;

      // Check for common error response formats
      if (parsed.detail && typeof parsed.detail === 'string') {
        return parsed.detail;
      }

      if (parsed.message && typeof parsed.message === 'string') {
        return parsed.message;
      }

      if (parsed.error && typeof parsed.error === 'string') {
        return parsed.error;
      }

      // Handle validation errors with field-specific messages
      if (parsed.errors && typeof parsed.errors === 'object') {
        const fieldErrors = Object.entries(parsed.errors)
          .map(([field, messages]) => {
            if (Array.isArray(messages)) {
              return `${field}: ${messages.join(', ')}`;
            }
            return `${field}: ${messages}`;
          })
          .join('; ');
        return fieldErrors || 'Validation error';
      }

      // If we can't find a specific message in the parsed JSON
      return 'An error occurred while processing your request';
    } catch {
      // If it's not valid JSON, return the original message
      // but check if it looks like raw JSON first (starts with { or [)
      const trimmed = error.message.trim();
      if (
        (trimmed.startsWith('{') || trimmed.startsWith('[')) &&
        (trimmed.endsWith('}') || trimmed.endsWith(']'))
      ) {
        return 'An error occurred while processing your request';
      }
      return error.message;
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    // Check if the string looks like JSON
    const trimmed = error.trim();
    if (
      (trimmed.startsWith('{') || trimmed.startsWith('[')) &&
      (trimmed.endsWith('}') || trimmed.endsWith(']'))
    ) {
      // Try to parse it and extract a meaningful message
      try {
        const parsed = JSON.parse(error) as ApiErrorResponse;
        if (parsed.detail && typeof parsed.detail === 'string') {
          return parsed.detail;
        }
        if (parsed.message && typeof parsed.message === 'string') {
          return parsed.message;
        }
      } catch {
        // If parsing fails, return generic message
        return 'An error occurred while processing your request';
      }
    }
    return error;
  }

  // Default fallback
  return 'An unexpected error occurred';
}

/**
 * Creates a standardized error handler for API calls
 * @param context - Context string for better error messages (e.g., "Failed to create project")
 * @returns Error handler function
 */
export function createErrorHandler(context: string) {
  return (error: unknown): string => {
    const baseMessage = getErrorMessage(error);

    // If the error message is generic, prepend the context
    if (
      baseMessage === 'An error occurred while processing your request' ||
      baseMessage === 'An unexpected error occurred'
    ) {
      return `${context}: ${baseMessage}`;
    }

    // If we have a specific error message, use it directly
    return baseMessage;
  };
}
